import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	CardBody,
	FormField,
	Input,
	Modal,
	Page,
	Select,
	Table,
	type TableColumn,
	useToast,
} from "@theg1group/ui";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AddWalletModal } from "../components/AddWalletModal.tsx";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { GraphView } from "../components/GraphView.tsx";
import { KpiTile } from "../components/KpiTile.tsx";
import { PartnerForm } from "../components/PartnerForm.tsx";
import { PartnerStatusBadge } from "../components/PartnerStatusBadge.tsx";
import { RiskBadge } from "../components/RiskBadge.tsx";
import { TraceProgress, jobCounts } from "../components/TraceProgress.tsx";
import { api } from "../lib/api.ts";
import {
	fmtAgo,
	fmtDate,
	fmtNum,
	fmtUsd,
	isJobActive,
	jobStatusTone,
	partnerStatusLabel,
	severityTone,
	statusTone,
} from "../lib/format.ts";
import type {
	PartnerDetailData,
	PartnerLink,
	PartnerStatus,
	PartnerWallet,
	TraceDirection,
	TraceInput,
	TraceJob,
} from "../lib/types.ts";
import { KindBadge } from "./Overlap.tsx";

export function PartnerDetail() {
	const { id = "" } = useParams();
	const qc = useQueryClient();
	const toast = useToast();
	const navigate = useNavigate();
	const [showEdit, setShowEdit] = useState(false);
	const [showAdd, setShowAdd] = useState(false);
	const [confirmStatus, setConfirmStatus] = useState<PartnerStatus | null>(null);
	const [confirmDelete, setConfirmDelete] = useState(false);

	const detail = useQuery({
		queryKey: ["partner", id],
		queryFn: () => api.get<PartnerDetailData>(`/api/partners/${id}`),
		enabled: !!id,
		refetchInterval: (q) =>
			q.state.data?.traces.some((t) => isJobActive(t.status)) ? 5_000 : false,
	});

	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["partner", id] });
		qc.invalidateQueries({ queryKey: ["partners"] });
	};

	const setStatus = useMutation({
		mutationFn: (status: PartnerStatus) => api.patch(`/api/partners/${id}`, { status }),
		onSuccess: (_r, status) => {
			toast.addToast("success", `Status → ${partnerStatusLabel(status)}`);
			invalidate();
			setConfirmStatus(null);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const del = useMutation({
		mutationFn: () => api.del(`/api/partners/${id}`),
		onSuccess: () => {
			toast.addToast("success", "Partner deleted");
			qc.invalidateQueries({ queryKey: ["partners"] });
			navigate("/partners");
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const setWalletNotify = useMutation({
		mutationFn: (v: { walletId: string; notifyMode: string }) =>
			api.patch(`/api/partners/${id}/wallets/${v.walletId}`, { notifyMode: v.notifyMode }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["partner", id] }),
		onError: (e) => toast.addToast("error", String(e)),
	});
	const removeWallet = useMutation({
		mutationFn: (walletId: string) => api.del(`/api/partners/${id}/wallets/${walletId}`),
		onSuccess: () => {
			toast.addToast("success", "Wallet removed");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const toggleWatch = useMutation({
		mutationFn: (w: PartnerWallet) =>
			api.post(`/api/addresses/${w.addressId}/watch`, { enabled: !w.watch?.enabled }),
		onSuccess: invalidate,
		onError: (e) => toast.addToast("error", String(e)),
	});

	const d = detail.data;
	const p = d?.partner;
	const riskScore = d?.riskScore ?? p?.riskScore ?? 0;
	const riskDetail = d?.riskDetail ?? p?.riskDetail ?? null;
	const riskWhy = (() => {
		if (!riskDetail) return null;
		const parts: string[] = [];
		if (riskDetail.fraudLinks)
			parts.push(`${riskDetail.fraudLinks} fraud link${riskDetail.fraudLinks === 1 ? "" : "s"}`);
		if (riskDetail.sanctioned) parts.push("sanctioned");
		if (riskDetail.topKind) parts.push(riskDetail.topKind.replaceAll("_", " "));
		return parts.length ? parts.join(" · ") : null;
	})();

	function requestStatus(next: PartnerStatus) {
		if (!p || next === p.status) return;
		if (next === "terminated_fraud") setConfirmStatus(next);
		else setStatus.mutate(next);
	}

	const linkCols: TableColumn<PartnerLink>[] = [
		{
			id: "partner",
			header: "Partner",
			accessor: (l) => (
				<Link
					to={`/partners/${l.otherPartnerId}`}
					className={`text-primary hover:underline ${l.dismissedAt ? "line-through opacity-60" : ""}`}
				>
					{l.otherPartnerName}
				</Link>
			),
		},
		{
			id: "kind",
			header: "Kind",
			accessor: (l) => (
				<span className={l.dismissedAt ? "opacity-60" : ""}>
					<KindBadge kind={l.kind} />
				</span>
			),
		},
		{
			id: "score",
			header: "Score",
			accessor: (l) => (
				<span
					className={
						l.dismissedAt
							? "text-muted line-through"
							: Number(l.score) >= 60
								? "text-danger font-semibold"
								: "text-content"
					}
				>
					{Number(l.score).toFixed(0)}
				</span>
			),
		},
		{
			id: "state",
			header: "",
			accessor: (l) => (l.dismissedAt ? <Badge size="sm">dismissed</Badge> : null),
		},
	];

	const walletCols: TableColumn<PartnerWallet>[] = [
		{ id: "chain", header: "Chain", accessor: (w) => <ChainBadge chain={w.chain} /> },
		{
			id: "address",
			header: "Address",
			accessor: (w) => (
				<div>
					<AddressLink
						id={w.addressId}
						address={w.address}
						display={w.display}
						head={10}
						tail={6}
					/>
					{w.label && <div className="text-[11px] text-muted">{w.label}</div>}
				</div>
			),
		},
		{
			id: "notify",
			header: "Notify",
			accessor: (w) => (
				<Select
					size="sm"
					value={w.notifyMode}
					onChange={(e) => setWalletNotify.mutate({ walletId: w.id, notifyMode: e.target.value })}
					options={[
						{ value: "both", label: "Both" },
						{ value: "inbound", label: "Inbound" },
						{ value: "outbound", label: "Payouts (out)" },
						{ value: "off", label: "Off" },
					]}
				/>
			),
		},
		{ id: "tx", header: "Tx", accessor: (w) => fmtNum(w.stats.txCount) },
		{ id: "in30", header: "In 30d", accessor: (w) => fmtUsd(w.stats.inUsd30d) },
		{
			id: "seen",
			header: "Last seen",
			accessor: (w) => <span title={fmtDate(w.stats.lastSeen)}>{fmtAgo(w.stats.lastSeen)}</span>,
		},
		{
			id: "watch",
			header: "Watch",
			accessor: (w) =>
				w.watch?.enabled ? (
					<span
						title={`polled ${fmtAgo(w.watch.lastPolledAt)} · next ${fmtDate(w.watch.nextPollAt)}`}
					>
						<Badge variant="success">on</Badge>
					</span>
				) : (
					<Badge>off</Badge>
				),
		},
		{
			id: "added",
			header: "Added",
			accessor: (w) => <span title={w.addedBy ?? undefined}>{fmtDate(w.addedAt, false)}</span>,
		},
		{
			id: "actions",
			header: "",
			accessor: (w) => (
				<div className="flex gap-1.5">
					<Button size="sm" variant="ghost" onClick={() => toggleWatch.mutate(w)}>
						{w.watch?.enabled ? "Unwatch" : "Watch"}
					</Button>
					<Button
						size="sm"
						variant="danger"
						onClick={() => {
							if (confirm(`Remove wallet ${w.display}?`)) removeWallet.mutate(w.id);
						}}
					>
						Remove
					</Button>
				</div>
			),
		},
	];

	if (detail.isLoading) return <Page title="Partner">Loading…</Page>;
	if (!d || !p) return <Page title="Partner">Not found.</Page>;

	return (
		<Page
			title={
				<span className="inline-flex items-center gap-2">
					{p.name} <PartnerStatusBadge status={p.status} />
					{p.source === "affiliates" && p.affStatus && p.affStatus !== p.status && (
						<Badge variant={p.affStatus === "missing" ? "error" : statusTone(p.affStatus)}>
							aff: {p.affStatus}
						</Badge>
					)}
				</span>
			}
			description={[p.externalRef, `created ${fmtDate(p.createdAt, false)}`]
				.filter(Boolean)
				.join(" · ")}
			actions={
				<div className="flex flex-wrap gap-2 items-center">
					{p.source === "affiliates" && p.affUrl && (
						<a
							href={p.affUrl}
							target="_blank"
							rel="noreferrer"
							className="text-primary text-sm hover:underline"
						>
							Open in Affiliates ↗
						</a>
					)}
					<Link
						to={`/graph?focus=${encodeURIComponent(`partner:${p.id}`)}`}
						className="text-primary text-sm hover:underline"
					>
						Open in graph →
					</Link>
					<div className="w-48">
						<Select
							value={p.status}
							onChange={(e) => requestStatus(e.target.value as PartnerStatus)}
							options={[
								{ value: "active", label: "Active" },
								{ value: "paused", label: "Paused" },
								{ value: "terminated", label: "Terminated" },
								{ value: "terminated_fraud", label: "Terminated (fraud)" },
							]}
							size="sm"
						/>
					</div>
					<Button variant="secondary" onClick={() => setShowEdit(true)}>
						Edit
					</Button>
					<Button variant="danger" onClick={() => setConfirmDelete(true)}>
						Delete
					</Button>
				</div>
			}
		>
			<div
				className="grid gap-3.5 mb-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}
			>
				<KpiTile
					label="Risk"
					value={<RiskBadge score={riskScore} />}
					hint={riskWhy ?? undefined}
					tone={riskScore >= 60 ? "danger" : "default"}
				/>
				<KpiTile label="Wallets" value={fmtNum(d.wallets.length)} />
				<KpiTile
					label="Inbound 30d"
					value={fmtUsd(d.totals.inboundUsd30d)}
					hint={`${fmtNum(d.totals.inboundCount30d)} transfers`}
				/>
				<KpiTile label="Inbound 365d" value={fmtUsd(d.totals.inboundUsd365d)} />
				<KpiTile
					label="Open alerts"
					value={fmtNum(d.alerts.filter((a) => !a.readAt).length)}
					tone={d.alerts.some((a) => !a.readAt) ? "danger" : "default"}
				/>
				<KpiTile
					label="Link max"
					value={p.linkMax == null ? "—" : Number(p.linkMax).toFixed(2)}
					hint="overlap (phase 2)"
				/>
			</div>

			{(p.website || p.contactEmail) && (
				<Card className="mb-3.5">
					<CardBody>
						<div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-content">
							{p.website && (
								<span>
									<span className="text-[11px] uppercase text-muted mr-1.5">Website</span>
									<a
										href={p.website}
										target="_blank"
										rel="noreferrer"
										className="text-primary hover:underline"
									>
										{p.website}
									</a>
								</span>
							)}
							{p.contactEmail && (
								<span>
									<span className="text-[11px] uppercase text-muted mr-1.5">Contact</span>
									<a href={`mailto:${p.contactEmail}`} className="text-primary hover:underline">
										{p.contactEmail}
									</a>
								</span>
							)}
							{p.syncedAt && (
								<span className="text-muted text-xs self-center">synced {fmtAgo(p.syncedAt)}</span>
							)}
						</div>
					</CardBody>
				</Card>
			)}

			{p.notes && (
				<Card className="mb-3.5">
					<CardBody>
						<div className="text-[11px] uppercase text-muted mb-1">Notes</div>
						<div className="text-sm text-content whitespace-pre-wrap">{p.notes}</div>
					</CardBody>
				</Card>
			)}

			<Card className="mb-3.5">
				<CardBody>
					<div className="flex items-center justify-between mb-3">
						<h2 className="text-sm font-semibold text-content">Wallets ({d.wallets.length})</h2>
						<Button size="sm" variant="primary" onClick={() => setShowAdd(true)}>
							Add wallet
						</Button>
					</div>
					<div className="overflow-x-auto">
						<Table
							columns={walletCols}
							data={d.wallets}
							keyExtractor={(w) => w.id}
							emptyMessage="No wallets yet — add the partner's payout addresses."
						/>
					</div>
				</CardBody>
			</Card>

			<Card className={`mb-3.5 ${p.status === "terminated_fraud" ? "ring-2 ring-danger" : ""}`}>
				<CardBody>
					<div className="flex items-center justify-between mb-3">
						<h2 className="text-sm font-semibold text-content">Partner links ({d.links.length})</h2>
						<Link to="/overlap" className="text-primary text-xs hover:underline">
							Overlap matrix →
						</Link>
					</div>
					{p.status === "terminated_fraud" && d.links.length > 0 && (
						<div className="text-xs text-danger mb-2">
							Fraud partner — linked partners below may be the same operator.
						</div>
					)}
					<div className="overflow-x-auto">
						<Table
							columns={linkCols}
							data={d.links}
							keyExtractor={(l) => l.id}
							emptyMessage="No overlap links to other partners."
						/>
					</div>
				</CardBody>
			</Card>

			<Card className="mb-3.5">
				<CardBody>
					<h2 className="text-sm font-semibold text-content mb-3">Graph (depth 1)</h2>
					<GraphView
						focus={`partner:${p.id}`}
						depth={1}
						minUsd={0}
						height="480px"
						onFocus={(f) => navigate(`/graph?focus=${encodeURIComponent(f)}`)}
					/>
				</CardBody>
			</Card>

			<div
				className="grid gap-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))" }}
			>
				<Card>
					<CardBody>
						<h2 className="text-sm font-semibold text-content mb-3">Traces</h2>
						<StartTraceForm partnerId={p.id} onStarted={invalidate} />
						<TracesList traces={d.traces} />
					</CardBody>
				</Card>
				<Card>
					<CardBody>
						<h2 className="text-sm font-semibold text-content mb-3">Alerts</h2>
						{d.alerts.map((a) => (
							<div key={a.id} className="flex items-start gap-2 text-sm mb-2">
								<Badge variant={severityTone(a.severity)}>{a.severity}</Badge>
								<div className="min-w-0 flex-1">
									<div className={`truncate ${a.readAt ? "text-muted" : "text-content"}`}>
										{a.title}
									</div>
									<div className="text-[11px] text-muted">
										{a.kind} · {fmtAgo(a.createdAt)}
										{a.addressId && (
											<>
												{" · "}
												<Link
													to={`/addresses/${a.addressId}`}
													className="text-primary hover:underline"
												>
													address
												</Link>
											</>
										)}
									</div>
								</div>
							</div>
						))}
						{!d.alerts.length && <div className="text-xs text-muted">No alerts.</div>}
					</CardBody>
				</Card>
			</div>

			<PartnerForm
				open={showEdit}
				onClose={() => setShowEdit(false)}
				partner={p}
				nameLocked={p.source === "affiliates"}
			/>
			<AddWalletModal partnerId={p.id} open={showAdd} onClose={() => setShowAdd(false)} />

			<Modal
				open={!!confirmStatus}
				onClose={() => setConfirmStatus(null)}
				title="Mark as terminated (fraud)?"
				footer={
					<div className="flex justify-end gap-2">
						<Button variant="ghost" onClick={() => setConfirmStatus(null)}>
							Cancel
						</Button>
						<Button
							variant="danger"
							loading={setStatus.isPending}
							onClick={() => confirmStatus && setStatus.mutate(confirmStatus)}
						>
							Confirm
						</Button>
					</div>
				}
			>
				<p className="text-sm text-content">
					This flags <strong>{p.name}</strong> as a fraud partner. Overlap links to other partners
					will be re-evaluated and their wallets become high-risk counterparties in traces.
				</p>
			</Modal>

			<Modal
				open={confirmDelete}
				onClose={() => setConfirmDelete(false)}
				title="Delete partner?"
				footer={
					<div className="flex justify-end gap-2">
						<Button variant="ghost" onClick={() => setConfirmDelete(false)}>
							Cancel
						</Button>
						<Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>
							Delete
						</Button>
					</div>
				}
			>
				<p className="text-sm text-content">
					Deletes <strong>{p.name}</strong> and unlinks its {d.wallets.length} wallet(s). Addresses
					and their history stay in the graph.
				</p>
			</Modal>
		</Page>
	);
}

function TracesList({ traces }: { traces: TraceJob[] }) {
	if (!traces.length) return <div className="text-xs text-muted mt-3">No traces yet.</div>;
	return (
		<div className="flex flex-col gap-2 mt-3">
			{traces.map((t) => (
				<div key={t.id} className="rounded-md border border-line p-2">
					<div className="flex items-center justify-between text-xs mb-1">
						<Link to={`/traces/${t.id}`} className="text-primary hover:underline font-mono">
							{t.id.slice(0, 8)}…
						</Link>
						<span className="text-muted">
							{t.kind} · depth {t.params?.depth ?? "?"} · {fmtAgo(t.createdAt)}
						</span>
						<Badge variant={jobStatusTone(t.status)}>{t.status}</Badge>
					</div>
					<TraceProgress counts={jobCounts(t)} compact />
					{t.error && <div className="text-[11px] text-danger mt-1 break-all">{t.error}</div>}
				</div>
			))}
		</div>
	);
}

const DIRS: Array<{ value: TraceDirection; label: string }> = [
	{ value: "in", label: "Inbound" },
	{ value: "out", label: "Outbound" },
	{ value: "both", label: "Both" },
];

export function StartTraceForm({
	partnerId,
	seedAddressIds,
	onStarted,
}: {
	partnerId?: string;
	seedAddressIds?: string[];
	onStarted?: (jobId: string) => void;
}) {
	const toast = useToast();
	const qc = useQueryClient();
	const [open, setOpen] = useState(false);
	const [depth, setDepth] = useState(3);
	const [fanout, setFanout] = useState(25);
	const [budget, setBudget] = useState(2000);
	const [minUsd, setMinUsd] = useState(100);
	const [direction, setDirection] = useState<TraceDirection>("in");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");

	const start = useMutation({
		mutationFn: (body: TraceInput) => api.post<{ jobId: string }>("/api/traces", body),
		onSuccess: (r) => {
			toast.addToast("success", `Trace ${r.jobId.slice(0, 8)}… started`);
			qc.invalidateQueries({ queryKey: ["traces"] });
			onStarted?.(r.jobId);
			setOpen(false);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	if (!open) {
		return (
			<Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
				Start deep trace
			</Button>
		);
	}
	return (
		<div className="rounded-md border border-line bg-surface-2 p-3">
			<div className="grid grid-cols-3 gap-2">
				<FormField label="Depth" htmlFor="tr-depth">
					<Input
						id="tr-depth"
						type="number"
						min={1}
						max={6}
						value={depth}
						onChange={(e) => setDepth(Number(e.target.value))}
						size="sm"
					/>
				</FormField>
				<FormField label="Fanout" htmlFor="tr-fanout">
					<Input
						id="tr-fanout"
						type="number"
						min={1}
						max={200}
						value={fanout}
						onChange={(e) => setFanout(Number(e.target.value))}
						size="sm"
					/>
				</FormField>
				<FormField label="Budget nodes" htmlFor="tr-budget">
					<Input
						id="tr-budget"
						type="number"
						min={10}
						value={budget}
						onChange={(e) => setBudget(Number(e.target.value))}
						size="sm"
					/>
				</FormField>
				<FormField label="Min USD" htmlFor="tr-minusd">
					<Input
						id="tr-minusd"
						type="number"
						min={0}
						value={minUsd}
						onChange={(e) => setMinUsd(Number(e.target.value))}
						size="sm"
					/>
				</FormField>
				<FormField label="Direction" htmlFor="tr-dir">
					<Select
						id="tr-dir"
						value={direction}
						onChange={(e) => setDirection(e.target.value as TraceDirection)}
						options={DIRS}
						size="sm"
					/>
				</FormField>
				<FormField label="From" htmlFor="tr-from">
					<Input
						id="tr-from"
						type="date"
						value={from}
						onChange={(e) => setFrom(e.target.value)}
						size="sm"
					/>
				</FormField>
				<FormField label="To" htmlFor="tr-to">
					<Input
						id="tr-to"
						type="date"
						value={to}
						onChange={(e) => setTo(e.target.value)}
						size="sm"
					/>
				</FormField>
			</div>
			<div className="flex gap-2 mt-2">
				<Button
					size="sm"
					variant="primary"
					loading={start.isPending}
					onClick={() =>
						start.mutate({
							partnerId,
							seedAddressIds,
							depth,
							fanout,
							budgetNodes: budget,
							minUsd,
							direction,
							from: from || undefined,
							to: to || undefined,
						})
					}
				>
					Start
				</Button>
				<Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
					Cancel
				</Button>
			</div>
		</div>
	);
}
